'use client';

import { useBodyScrollLock } from '@common/hooks';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { Footer } from '@/app/_components/Footer';
import { FooterPc } from '@/app/_components/Footer/_components/FooterPc';
import { BREAKPOINTS } from '@/constants';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useGlobalStore } from '@/stores/GlobalStore';

const HomeFooterModal = ({
  open,
  onClose,
  onOpenTerms,
}: {
  open: boolean;
  onClose: () => void;
  onOpenTerms: () => void;
}) => {
  useBodyScrollLock(open);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          className="fixed inset-0 z-30 flex items-end justify-center bg-Gray-black/50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className="w-full bg-Gray-white"
            initial={{ y: 32, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 32, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            onClick={(e) => e.stopPropagation()}
          >
            <FooterPc onOpenTerms={onOpenTerms} />
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
};

export const HomeFooterSection = () => {
  const isDesktopViewport = useMediaQuery(BREAKPOINTS.DESKTOP, true);
  const reopenFooterAfterTermsCloseRef = useRef(false);
  const waitingForTermsOpenRef = useRef(false);
  const [isHomeFooterModalOpen, setIsHomeFooterModalOpen, termsDrawerType] =
    useGlobalStore(
      useShallow(
        (state) =>
          [
            state.isHomeFooterModalOpen,
            state.setIsHomeFooterModalOpen,
            state.termsDrawerType,
          ] as const,
      ),
    );

  useEffect(() => {
    if (!isDesktopViewport) {
      reopenFooterAfterTermsCloseRef.current = false;
      waitingForTermsOpenRef.current = false;
      setIsHomeFooterModalOpen(false);
    }
  }, [isDesktopViewport, setIsHomeFooterModalOpen]);

  useEffect(() => {
    return () => {
      reopenFooterAfterTermsCloseRef.current = false;
      waitingForTermsOpenRef.current = false;
      setIsHomeFooterModalOpen(false);
    };
  }, [setIsHomeFooterModalOpen]);

  useEffect(() => {
    if (!isDesktopViewport) return;

    if (termsDrawerType) {
      waitingForTermsOpenRef.current = false;

      if (isHomeFooterModalOpen) {
        reopenFooterAfterTermsCloseRef.current = true;
        setIsHomeFooterModalOpen(false);
      }

      return;
    }

    if (
      !waitingForTermsOpenRef.current &&
      reopenFooterAfterTermsCloseRef.current
    ) {
      reopenFooterAfterTermsCloseRef.current = false;
      setIsHomeFooterModalOpen(true);
    }
  }, [
    isDesktopViewport,
    isHomeFooterModalOpen,
    setIsHomeFooterModalOpen,
    termsDrawerType,
  ]);

  return (
    <>
      <Footer />
      {isDesktopViewport ? (
        <HomeFooterModal
          open={isHomeFooterModalOpen}
          onClose={() => setIsHomeFooterModalOpen(false)}
          onOpenTerms={() => {
            waitingForTermsOpenRef.current = true;
            reopenFooterAfterTermsCloseRef.current = true;
            setIsHomeFooterModalOpen(false);
          }}
        />
      ) : null}
    </>
  );
};
